import { useEffect } from "react";
import type { SpatialEntity, SpatialState } from "../types/game";

interface Props {
  targetId: string;
  spatialState: SpatialState | null;
  onUnlock?: () => void;
}

const CONDITION_COLORS: Record<string, string> = {
  healthy: "var(--color-green)",
  damaged: "var(--color-yellow)",
  badly_damaged: "var(--color-orange)",
  critical: "var(--color-red)",
  destroyed: "var(--color-red)",
};

export function TargetLock({ targetId, spatialState, onUnlock }: Props) {
  if (!spatialState) {
    return (
      <div className="target-lock">
        <div className="target-lock-empty">NO DATA</div>
      </div>
    );
  }

  const entity = spatialState.entities.find(e => e.id === targetId);

  // Auto-dismiss TARGET LOST after 5 seconds
  useEffect(() => {
    if (entity) return;
    const timer = setTimeout(() => onUnlock?.(), 5000);
    return () => clearTimeout(timer);
  }, [entity, onUnlock]);

  if (!entity) {
    return (
      <div className="target-lock">
        <div className="target-lock-empty">TARGET LOST</div>
        {onUnlock && <button className="target-lock-unlock" onClick={onUnlock}>DISMISS</button>}
      </div>
    );
  }

  const pp = spatialState.playerShip.position;
  const dx = entity.position.x - pp.x;
  const dy = entity.position.y - pp.y;
  const dz = entity.position.z - pp.z;
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

  // Bearing relative to player heading
  const hd = spatialState.playerShip.heading;
  const headAngle = Math.atan2(hd.z, hd.x);
  const entAngle = Math.atan2(dz, dx);
  const bearing = ((entAngle - headAngle) * 180 / Math.PI + 360) % 360;

  return (
    <div className="target-lock">
      <div className="target-lock-header">
        <span className="target-lock-name">{entity.name.toUpperCase()}</span>
        {entity.cloaked && <span className="target-lock-cloaked">CLOAKED</span>}
      </div>

      <div className="status-divider" />

      <div className="status-section">
        {entity.shipClass && (
          <Row label="CLASS" value={entity.shipClass.toUpperCase()} />
        )}
        {entity.faction && (
          <Row label="FACTION" value={entity.faction.toUpperCase()} />
        )}
      </div>

      <div className="status-divider" />

      <div className="status-section">
        <Row label="BEARING" value={`${String(Math.round(bearing)).padStart(3, "0")}°`} />
        <Row label="RANGE" value={formatRange(dist)} />
      </div>

      <div className="status-divider" />

      <EntityStatus entity={entity} />
    </div>
  );
}

function formatRange(gameUnits: number): string {
  const km = gameUnits * 1000;
  if (km < 1) return "<1 km";
  if (km >= 1000) return `${(km / 1000).toFixed(0)}k km`;
  return `${km.toFixed(0)} km`;
}

function Row({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="status-row">
      <span className="status-label">{label}</span>
      <span className="status-value" style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

function EntityStatus({ entity }: { entity: SpatialEntity }) {
  if (entity.type === "planet" || entity.type === "star" || entity.type === "jump_point") {
    return (
      <div className="status-section">
        <Row label="TYPE" value={entity.type.replace("_", " ").toUpperCase()} />
      </div>
    );
  }

  const rawShields = entity.shields ?? "down";
  const shields = typeof rawShields === "string" ? rawShields : (rawShields as any)?.status ?? "down";
  const rawCondition = entity.condition ?? "healthy";
  const condition = typeof rawCondition === "string" ? rawCondition : (rawCondition as any)?.status ?? "healthy";

  return (
    <div className="status-section">
      <Row
        label="SHIELDS"
        value={shields.toUpperCase()}
        color={shields === "up" ? "var(--color-green)" : "var(--color-text-dim)"}
      />
      <Row
        label="HULL"
        value={condition.replace("_", " ").toUpperCase()}
        color={CONDITION_COLORS[condition]}
      />
    </div>
  );
}
